import React from 'react';
import { useMountedState } from 'react-use';
import { useSelector } from 'react-redux';
import { ConfigurationSchema } from 'frontastic-common';
import { isPlainObject } from '../../../utils/assertions';
import { usePathname, useSearchParams } from 'next/navigation';
import { HeadPortal } from '@srcDS/components/organisms/headPortal/HeadPortal';

const formatHrefLang = (hrefLang, locale) => {
  return !isPlainObject(hrefLang) ? hrefLang : hrefLang?.[locale];
};

export const GlobalSchema = ({ showCanonicalLink }) => {
  const context = useSelector((state) => state.app.context);

  const isMounted = useMountedState();

  const projectSchema = new ConfigurationSchema(
    context.projectConfigurationSchema,
    context.projectConfiguration,
  );

  const storeUrl = context.projectConfiguration['EXTENSION_PUBLIC_URL'];

  const organizationName = projectSchema.get('schemaOrgName');
  const sameAs = projectSchema.get('schemaOrgSameAs');

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    url: `${storeUrl}`,
    name:
      organizationName && typeof organizationName === 'object'
        ? Object.values(organizationName)?.[0]
        : organizationName,
    logo: `${storeUrl}/logo.png`,
    sameAs:
      sameAs && typeof sameAs === 'object'
        ? Object.values(sameAs)?.[0]
        : sameAs?.split(','),
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: projectSchema.get('schemaOrgContactPhone'),
      contactType: 'customer support',
      areaServed: 'AU',
      availableLanguage: 'English',
    },
  };

  const canonicalUrl = new URL(storeUrl + usePathname());

  const hrefLangDefaultSchema = formatHrefLang(
    projectSchema?.get('hrefLangDefault'),
    context.locale,
  );
  const hrefLangEnAu = formatHrefLang(
    projectSchema?.get('hrefLangEnAu'),
    context.locale,
  );
  const hrefLangEnNz = formatHrefLang(
    projectSchema?.get('hrefLangEnNz'),
    context.locale,
  );

  const hrefLangDefaultStr = hrefLangDefaultSchema || storeUrl;

  const hrefLangDefaultUrl =
    hrefLangDefaultStr &&
    new URL(hrefLangDefaultStr + usePathname());

  const hrefLangEnAuUrl =
    hrefLangEnAu && new URL(hrefLangEnAu + usePathname());

  const hrefLangEnNzUrl =
    hrefLangEnNz && new URL(hrefLangEnNz + usePathname());

  // The following extracts the mutable URLSearchParams instance from the
  // immutable variant supplied by useSearchParams(). The code after this hack
  // mutates the search params in some inscrutable way to unknown ends. Rather
  // than try and figure out what the troglodytes who wrote this "logic"
  // originally intended, we just make it work for them by giving them back
  // their mutable URLSearchParams.
  const readonlySearchParams = useSearchParams();
  const searchParams = new URLSearchParams(readonlySearchParams);

  const paramsToKeep = [
    // category offset param
    /^s\[.+\]\[offset\]$/g,
    // category limit param
    /^s\[.+\]\[limit\]$/g,
    // id param (for store locator)
    /^id$/g,
  ];

  for (const [key] of searchParams.entries()) {
    if (paramsToKeep.every((regex) => !regex.test(key)))
      searchParams.delete(key);
  }

  const search = searchParams.toString();
  canonicalUrl.search = search;
  hrefLangDefaultUrl.search = search;
  if (hrefLangEnAuUrl) {
    hrefLangEnAuUrl.search = search;
  }
  if (hrefLangEnNzUrl) {
    hrefLangEnNzUrl.search = search;
  }

  return (
    <HeadPortal>
        {hrefLangDefaultUrl && (
          <link
            rel="alternate"
            href={hrefLangDefaultUrl?.toString()}
            hrefLang="x-default"
          />
        )}
        {hrefLangEnAuUrl && (
          <link
            rel="alternate"
            href={hrefLangEnAuUrl?.toString()}
            hrefLang="en-au"
          />
        )}
        {hrefLangEnNzUrl && (
          <link
            rel="alternate"
            href={hrefLangEnNzUrl?.toString()}
            hrefLang="en-nz"
          />
        )}
        {showCanonicalLink && (
          <link rel="canonical" href={canonicalUrl.toString()} />
        )}

      {isMounted() && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
        />
      )}
    </HeadPortal>
  );
};
